package com.shadowings.gero.data.lessons

import com.shadowings.gero.data.repository.Hiero
import com.shadowings.gero.presentation.model.Verb
import com.shadowings.gero.presentation.model.Word
import kotlinx.coroutines.sync.Mutex

// The only vocabulary a quiz may use for its wrong options (word-bank tiles,
// filler choices): words/signs from lessons the player already completed plus
// the ones the current lesson introduces — never the whole words.tsv/hiero.tsv,
// or a row added for a future lesson would leak into earlier quizzes.
// Built by SentenceRepository.getDistractorPool.
class DistractorPool(
    val wordIds: Set<String>,
    val hieroCodes: Set<String>
) {
    // A flessione (plural/feminine) counts through its base word, like
    // SentenceRepository's seen-word filter: GATTI is allowed once GATTO is.
    fun allowsWord(word: Word): Boolean =
        word.id in wordIds || (word.baseWordId != null && word.baseWordId in wordIds)

    // Every verbs.tsv key is also a words.tsv id.
    fun allowsVerb(verb: Verb): Boolean = verb.chiaveWords in wordIds

    fun allowsHiero(hiero: Hiero): Boolean = hiero.gardinerCode in hieroCodes

    companion object {
        // Set only for the duration of LessonContentDataSource.getQuizContent,
        // so the leaf distractor builders can read it without threading a
        // parameter through every lesson's quiz function. null (tests,
        // previews, anything outside a quiz build) means "no restriction".
        private var current: DistractorPool? = null

        // current is one global slot, so two overlapping quiz builds (e.g. a
        // QuizViewModel cleared mid-build while the next one starts — the
        // build is synchronous and doesn't stop on cancellation) would
        // restore each other's pool. Callers that pass a pool must hold this
        // lock around the whole getQuizContent call; take it with withLock,
        // which releases it in a finally even on exception or cancellation.
        val buildLock = Mutex()

        fun <T> using(pool: DistractorPool?, block: () -> T): T {
            val previous = current
            current = pool
            try {
                return block()
            } finally {
                current = previous
            }
        }

        fun words(candidates: List<Word>): List<Word> =
            current?.let { pool -> candidates.filter(pool::allowsWord) } ?: candidates

        fun verbs(candidates: List<Verb>): List<Verb> =
            current?.let { pool -> candidates.filter(pool::allowsVerb) } ?: candidates

        fun hieros(candidates: List<Hiero>): List<Hiero> =
            current?.let { pool -> candidates.filter(pool::allowsHiero) } ?: candidates
    }
}
